import * as colors from 'scoreboard-colors'
import * as d3 from 'd3-selection'
import { EventEmitter } from 'events'
import {interpolate} from 'd3-interpolate'
import miter from './renderings/helpers/miter-limit.js'
import * as ease from 'd3-ease'

import 'd3-transition' // Mixin into d3-selection prototype

const DURATION = 250

const PATH_DURATION = 1000

const pathTweener = (d1, precision) => {
  return function () {
    const path0 = this // this is the current path element
    const path1 = path0.cloneNode()

    path1.setAttribute('d', d1) // Set the new path to the clone

    const n0 = path0.getTotalLength()
    const n1 = path1.getTotalLength()

    // Uniform sampling of distance based on specified precision
    const distances = [0]
    const dt = precision / Math.max(n0, n1)

    let i = 0
    while ((i += dt) < 1) {
      distances.push(i)
    }
    distances.push(1)

    // Compute point-interpolators at each distance
    const points = distances.map((t) => {
      const p0 = path0.getPointAtLength(t * n0)
      const p1 = path1.getPointAtLength(t * n1)
      return interpolate([p0.x, p0.y], [p1.x, p1.y])
    })

    return (t) => t < 1 ? `M${points.map((p) => p(t)).join('L')}` : d1
  }
}

export default class Symbol extends EventEmitter {

  /*
   * Apply mixins to the symbol class. Mixins are used to add additional
   * functionality to the symbol class. This is useful for adding
   * functionality that is shared across multiple symbols.
   *
   * We have to use a static method to apply the mixins b/c the constructor
   * doesn't have access to the child's instance properties.
   *
   * This approach leverages the fact that static properties and
   * methods are accessible through class inheritance.
   */
  static applyMixins (symbol) {
    if (this.mixins) {
      this.mixins.forEach(mixin => {
        Object.assign(symbol, mixin)
      })
    }
  }

  minDimensions = { width: 10, height: 10 }

  constructor (id, data) {
    super()
    this.id = id
    this.type = data.symbol
    this.data = data // Initial set

    this.constructor.applyMixins(this)
  }

  setData (data, dimensions) {
    this.data = data
    this.data.strokeWidth = this._calculateStrokeWidth(data, dimensions)
  }

  /*
   * Calculate the stroke width based on the dimensions and data.strokeWidth
   * We need to make sure the stroke width doesn't exceed half the smallest dimension b/c
   * we subtract the stroke width from the dimensions. If the stroke width is too large,
   * the dimensions (width or height) will be negative, which causes issues with rendering.
   */
  _calculateStrokeWidth (data, dimensions) {
    const min = Math.min(dimensions.width, dimensions.height) // Use the smaller of the two dimensions

    // Ensure the stroke width doesn't exceed half the smallest dimension
    return Math.min(parseInt(data.strokeWidth) || 0, min / 2)
  }

  _getDefaultHandles () {
    return this.handles?.map((handle, idx) => {
      let coords = this.data.handles?.[idx] || handle.defaults

      return Object.assign(handle, {
        idx,
        coords
      })
    }) || []
  }

  /*
   * Returns the data (handle coords by default) that should be used
   * when broadcasting a change:data event with new handles that should be
   * persisted.
   *
   * By default it just returns the data.handles array, but some symbols
   * might not want to store all the handles or broadcast additional data.
   *  e.g. the callout symbol
   */
  handleChangeData (handles) {
    return {
      handles: handles.map(handle => handle.coords)
    }
  }

  /*
   * Returns the handles for this symbol.
   * Subclasses should override this method if they need to dynamically
   * generate handles.
   */
  getHandles (dimensions) {
    const handles = this._getDefaultHandles()

    // Make sure the handles are within the constraints
    handles.forEach((handle, idx, handles) => {
      this.enforceConstraints(handle, dimensions, handles)
    })
    return handles
  }

  /*
   * Similar to getPaths, but returns the paths in a flattened format.
   */
  getFlattenedPaths (data, dimensions) {
    // By default, no-op. 3d symbols will override this method
  }

  /*
   * Can be overridden by subclasses to adjust a previous handle location based
   * on the new dimensions.
   */
  onDimensionsChange (dimensions, handles) {
    // By default, do nothing
  }

  // TODO this actually modifies the handle in place, so we should change the name
  // of this method to reflect that.
  enforceConstraints (handle, dimensions, handles, pos = handle.coords) {
    // Grab the constraints for the handle. the constraints can be an array
    // or a function which returns an array.
    const [x, y] = Array.isArray(handle.constraints) ? handle.constraints : handle.constraints(dimensions, handles, pos)

    if (x !== null) {
      let bounds = Array.isArray(x) ? x : [x, x]
      handle.coords[0] = Math.min(Math.max(bounds[0], pos[0]), bounds[1])
    }
    if (y !== null) {
      let bounds = Array.isArray(y) ? y : [y, y]
      handle.coords[1] = Math.min(Math.max(bounds[0], pos[1]), bounds[1])
    }

    return handle
  }

  /*
   * Draws the gradient(s) for this symbol.
   */
  gradients (selection, duration) {
    let grad = selection.selectAll('.gradient').data(d => d.filter(d => d.gradient))

    grad.exit().remove()

    let enter = grad.enter()
      .append(d => document.createElementNS('http://www.w3.org/2000/svg', d.gradient.type))
      .attr('class', 'gradient')
      .attr('id', d => `${d.id}-gradient`)
      .attr('x1', d => d.gradient.x1)
      .attr('y1', d => d.gradient.y1)
      .attr('x2', d => d.gradient.x2)
      .attr('y2', d => d.gradient.y2)

    grad = grad.merge(enter)
      .selectAll('stop')
      .data(d => d.gradient.stops)
      .join('stop')
        .attr('offset', d => d.offset)
        .transition()
        .duration(duration)
        .attr('stop-color', d => d.color)
  }

  /*
   * Draws the clip paths for this symbol.
   */
  clips (selection) {
    selection.selectAll('.clip')
      .data(d => {
        // If any of the paths declare a clip path, use that, but
        // make sure we don't have duplicates
        return d.filter(c => c.clip)
          .map(c => c.clip)
          .filter((c, i, self) => self.indexOf(c) === i)
      })
      .join('clipPath')
      .attr('class', 'clip')
      .attr('id', d => `${d.id}-clip`)
      .selectAll('path')
      .data(d => [d])
      .join('path')
      .attr('d', d => d.path)
  }

  getFillValue (id, fillSetting, color) {
    if (!fillSetting || fillSetting === 'none') {
      return 'transparent' // No fill, use `transparent` to allow for hover events
    } else if (fillSetting === 'solid') {
      return colors.adjustLightness(color, 5) // Solid fill, lighten the color 5%
    } else {
      return `url(#${id}-gradient)`
    }
  }

  getComponents (data, dimensions) {
    const handles = this.getHandles(dimensions)
    const paths = this.getPaths(data, dimensions, handles.map(handle => handle.coords))
    const flattenedPaths = paths.length > 1 ? this.getFlattenedPaths(data, dimensions) : paths

    return { handles, paths, flattenedPaths }
  }

  setStroke (d) {
    if (this.data.stroke) {
      // A specific stroke color is set, use it
      return this.data.stroke
    } else {
      // No stroke color set, so darken the fill color 20%
      return colors.adjustLightness(this.data.color, -20)
    }
  }

  /*
   * Draws the hover text for the symbol implemented as a basic
   * `title` element within each path.
   */
  _hoverText (selection) {
    selection.each(function (d) {
      selection.selectAll('title')
        .data([d.hoverText])
        .join('title')
        .text(d => d)
    })
  }

  _draw (selection, strokeWidth, dimensions, handles) {
    selection
      .attr('transform', d => d.main.translate ? `translate(${d.main.translate})` : null)
      .style('fill', d => d.main.styles?.fill || d.main.fill)
      .style('stroke', d => d.main.styles?.stroke || this.setStroke(d))
      .style('stroke-width', d => `${d.main.styles?.strokeWidth || strokeWidth}px`)
      .style('stroke-miterlimit', d => this.getMiter(d.main, dimensions, handles))
      .select(function (d) {
        // This select function is outside of a transition. We have to reset the fill
        // b/c the interpolation might have set a gradient URL to something that isn't accurate
        d3.select(this).style('fill', d => d.main.styles?.fill || d.main.fill)

        // Set any additional styles defined by the component
        const styles = d.main.styles || {}
        Object.keys(styles).forEach(key => {
          this.style[key] = styles[key]
        })
        return this
      })
  }

  _update (selection, duration, strokeWidth, dimensions, handles, pathEase = ease.easeCubic, pathUpdater) {
    if (!pathUpdater) {
      // Default path updater directly sets the path data. This can be used anytime
      // the number of control points doesn't change between paths. (i.e. the symbol is the same type)
      pathUpdater = function (selection) {
        selection.attr('d', d => d.main.path)
      }
    }

    const path = selection.selectAll('path.symbol').data(d => {
      return d.paths.map((path, i) => {
        return {
          hoverText: d.hoverText,
          main: path,
          flattened: d.flattenedPaths[i]
        }
      })
    })

    selection.selectAll('defs')
      .data(d => [d.paths])
      .join('defs')
      .call(this.gradients, duration)
      .call(this.clips)

    const enter = path.enter()
      .insert('path', function () {
        // Insert after the `defs`
        return d3.select(this).selectAll('defs').node().nextSibling
      })
      .attr('id', d => d.id)
      .attr('class', `symbol`)
      .call(this._draw.bind(this), strokeWidth, dimensions, handles)
      .attr('d', d => d.main.path) // Set the path data directly on enter

    const merged = path.merge(enter)
      .order() // Make sure the paths in the DOM match the order in the data
      .classed('symbol-outline', (d, i, all) => {
        // The last path is the outline. 2d shapes have one path, so that path automatically
        // becomes the outline.
        return i === all.length - 1
      })

    merged.call(this._hoverText)

    merged.transition()
      .duration(duration)
      .ease(pathEase)
      .call(this._draw.bind(this), strokeWidth, dimensions, handles)
      .call(pathUpdater)
      .end().then(() => {
        // Emit a rendered event when the paths have been updated
        this.emit('rendered', selection)
      }).catch(() => {
        // Interrupted transitions will throw an error, we can ignore
      })

    merged.filter(d => d.main.clip)
      .attr('clip-path', d => `url(#${d.main.clip.id}-clip)`)

    return merged
  }

  /*
   * We don't support animations when animating between two 3d shapes.
   * This is because the 3d shapes are so different that it's not worth
   * trying to animate between them. We'd need a 3 part transition, and
   * animating between the two flattened versions of the 3d shapes might be
   * a no-op. e.g. the flattened cube and cylinder are just rectangles.
   */
  _3dTo3d (selection, strokeWidth, dimensions, handles) {
    // Fade out the current paths
    selection.selectAll('path.symbol')
      .transition()
      .duration(PATH_DURATION / 2)
      .style('opacity', 0)
      .remove()
      .end().then(() => {
        // Now draw the new paths
        this._update(selection, PATH_DURATION / 2, strokeWidth, dimensions, handles)
          .style('opacity', 0) // Start with the new paths hidden
          .transition()
          .duration(PATH_DURATION / 2)
          .style('opacity', 1) // Fade in the new paths
      })
  }

  /*
   * When converting from a 3d shape to a 2d, we need to flatten the 3d shape
   * before transitioning to the 2d shape.
   */
  _3dTo2d (selection, _transition, strokeWidth, dimensions, handles) {
    // Start by getting the flattened paths for the current selection
    const flattened = selection.selectAll('path.symbol').data().map(d => d.flattened)
    const duration = _transition ? PATH_DURATION / 2 : 0

    selection.selectAll('path.symbol')
      .data(flattened)
      .style('stroke', this.setStroke.bind(this))
      .style('opacity', (d, i, all) => i === all.length - 1 ? 0 : 1) // Hide the last path, which is the outline
      .style('fill', d => d.fill)
      .transition()
      .duration(duration)
      .ease(ease.easeLinear)
      .attr('d', d => d.path)
      .style('opacity', 1)
      // Now remove all but the last path, which is the flattened symbol outline
      .filter((d, i, all) => i < all.length - 1)
      .remove()
      .end().then(() => {
        this._update(selection, duration, strokeWidth, dimensions, handles, ease.easeLinear, selection => {
          selection.attrTween('d', function (d) {
            // Transition the path to its flattened form
            return pathTweener(d.main.path, 1).bind(this)()
          })
        })
      })
  }

  /*
    * When converting from a 2d shape to a 3d shape, we need to transition the
    * flattened shape to the 3d shape.
    */
  _2dTo3d (selection, _transition, strokeWidth, dimensions, handles) {
    const prev = selection.selectAll('path.symbol').datum().main
    const duration = _transition ? PATH_DURATION / 2 : 0

    // Add the new paths
    const path = selection.selectAll('path.symbol').data(d => {
      return d.paths.map((path, i) => {
        return {
          main: path,
          flattened: d.flattenedPaths[i]
        }
      })
    })

    const enter = path.enter()
      .insert('path', function () {
        // Insert after the `defs`
        return d3.select(this).selectAll('defs').node().nextSibling
      }
      )
      .attr('id', d => d.id)
      .attr('class', `symbol`)
      .call(this._draw.bind(this), strokeWidth, dimensions, handles)
      .style('fill', prev.fill)
      .style('opacity', (d, i, all) => i < all.length - 1 ? 0 : 1) // Hide the last path, which is the outline
      .style('stroke', this.setStroke.bind(this))
      .attr('d', d => prev.path) // Set the path data directly

    // Now transition these paths to their flattened form
    path.merge(enter)
      .order()
      .transition()
      .duration(duration)
      .ease(ease.easeLinear)
      .call(this._draw.bind(this), strokeWidth, dimensions, handles)
      .attrTween('d', function (d) {
        // Transition the path to its flattened form
        return pathTweener(d.flattened.path, 1).bind(this)()
      })
      .end().then(() => {
        // Make sure they are all visible
        selection.selectAll('path.symbol').style('opacity', 1)
        this._update(selection, duration, strokeWidth, dimensions, handles, ease.easeLinear)
      })
  }

  _typeChange (selection, _transition, strokeWidth, dimensions, handles) {
    const is3d0 = selection.selectAll('path.symbol').size() > 1
    const is3d1 = selection.datum().paths.length > 1

    if (is3d0 && is3d1) {
      // 3d -> 3d
      return this._3dTo3d(selection, strokeWidth, dimensions, handles)
    } else if (!is3d0 && !is3d1) {
      // 2d -> 2d, basic update tweening between the two shapes
      return this._update(selection, _transition ? PATH_DURATION : 0, strokeWidth, dimensions, handles, undefined, selection => {
        selection.attrTween('d', function (d) {
          // Transition the path to its flattened form
          return pathTweener(d.main.path, 1).bind(this)()
        })
      })
    } else if (is3d0 && !is3d1) {
      // 3d -> 2d
      return this._3dTo2d(selection, _transition, strokeWidth, dimensions, handles)
    } else {
      // 2d -> 3d
      return this._2dTo3d(selection, _transition, strokeWidth, dimensions, handles)
    }
  }

  render (selection, _transition) {
    const { dimensions, handles } = selection.datum()

    const type0 = selection.attr('data-symbol')
    const type1 = this.type

    if (type0 === type1 || !type0) {
      // The types are the same, basic update
      return this._update(selection, _transition ? DURATION : 0, this.data.strokeWidth, dimensions, handles)
    } else {
      // The types are different, we need to transition between them
      return this._typeChange(selection, _transition, this.data.strokeWidth, dimensions, handles)
    }
  }

  getMiter (d) {
    return miter(d.path) * this.data.strokeWidth
  }

}
